// TODO: use appropriate openid connect library for nextjs instead
// https://nextjs.org/docs/authentication#authentication-providers
// e.g.: https://www.npmjs.com/package/react-oidc-context
//const getCookie = (name: string): string | null => {
//  const nameLenPlus = name.length + 1;
//  return (
//    document.cookie
//      .split(";")
//      .map((c) => c.trim())
//      .filter((cookie) => {
//        return cookie.substring(0, nameLenPlus) === `${name}=`;
//      })
//      .map((cookie) => {
//        return decodeURIComponent(cookie.substring(nameLenPlus));
//      })[0] || null
//  );
//};

import { NextRouter } from "next/router";
import * as Sentry from "@sentry/nextjs";
import { UserInterface } from "src/models/User";
import { viewAsTokenKey, normalTokenKey, profileKey } from "src/utils/authStorageKeys";

export const isLoggedIn = () => {
  return !isTokenExpiredOrNull();
};

const getViewAsToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return sessionStorage.getItem(viewAsTokenKey);
}

const getNormalToken = (): string | null => {
  //return getCookie("auth");
  return localStorage.getItem(normalTokenKey);
}

export const isTokenExpiredOrNull = (): boolean => {
  const token = getTokenToUse();
  if (!token || typeof window === 'undefined') {
    return true;
  }

  const tokenParts = token.split('.');
  const base64Url = tokenParts[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
  const payload = JSON.parse(window.atob(padded));

  const expirationTime = new Date(payload.exp * 1000);
  const now = new Date();
  return now > expirationTime;
}

export const setNormalToken = (token: string) => {
  //const expireTime = new Date(new Date().getTime() + 15 * 60 * 1000);
  //document.cookie = `auth=${token};expires=${expireTime};path=/`;
  localStorage.setItem(normalTokenKey, token);
}

export const setProfile = (user: UserInterface): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (getViewAsToken()) {
    setViewAsProfile(user);
  } else {
    localStorage.setItem(profileKey, JSON.stringify(user));
  }
  
  Sentry.setUser({ email: user.email })
}

export const setViewAsProfile = (user: UserInterface): void => {
  sessionStorage.setItem(profileKey, JSON.stringify(user));
}

export const getProfile = (): UserInterface | null => {
  if (typeof window === 'undefined' || isTokenExpiredOrNull()) {
    return null;
  }

  var sessionProfile = sessionStorage.getItem(profileKey);
  var profile = localStorage.getItem(profileKey);

  const parsedProfile = sessionProfile
    ? JSON.parse(sessionProfile)
    : profile
      ? JSON.parse(profile)
      : null;

  if (parsedProfile) {
    Sentry.setUser({ email: parsedProfile.email });
  } else {
    Sentry.setUser(null);
  }

  return parsedProfile;
}

let userUpdateCallback: (() => void) | null = null;

export const setUserUpdateCallback = (callback: (() => void) | null) => {
  userUpdateCallback = callback;
};

export const updateUserAllocationRights = (allocationRights: {
  canEditAllocations: number[];
  readOnlyAllocations: number[];
  canSendTicketsFromAllocation: number[];
}): void => {
  const currentProfile = getProfile();

  if (currentProfile) {
    const updatedProfile = {
      ...currentProfile,
      canEditAllocations: allocationRights.canEditAllocations,
      readOnlyAllocations: allocationRights.readOnlyAllocations,
      canSendTicketsFromAllocation: allocationRights.canSendTicketsFromAllocation,
    };

    // Update the appropriate storage based on whether view-as is active
    if (isViewAsActive()) {
      sessionStorage.setItem(profileKey, JSON.stringify(updatedProfile));
    } else {
      localStorage.setItem(profileKey, JSON.stringify(updatedProfile));
    }

    // Trigger the callback to refresh the user in React context
    if (userUpdateCallback) {
      userUpdateCallback();
    }
  }
}

export const isViewAsActive = (): boolean => {
  return Boolean(getViewAsToken());
}

const getTokenToUse = (): string | null => {
  const viewAsToken = getViewAsToken();

  return viewAsToken ? viewAsToken : getNormalToken();
}

export const setViewAsToken = async (token: string) => {
  sessionStorage.setItem(viewAsTokenKey, token);
  window.location.href = "/profile";
}


export const stopViewAs = async () => {
  sessionStorage.removeItem(viewAsTokenKey);
  sessionStorage.removeItem(profileKey);
  window.location.href = "/";
}

export const redirectToLoginPage = () => {
  window.location.href = `/login?redirectUrl=${window.location.pathname.split('?')[0]}`
}

export const fetchWithAuth = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const token = getTokenToUse();
  const isRequestLoginInfo = input.toString().includes("login-info");

  if ((!token || isTokenExpiredOrNull()) && !isRequestLoginInfo) {
    redirectToLoginPage();
  }

  const headers = {
    ...init?.headers,
    Authorization: `Bearer ${token}`,
  }

  const response = await defaultFetch(input, {
    ...init,
    headers
  });

  if (response.status === 403 && !isRequestLoginInfo && isTokenExpiredOrNull()) {
    redirectToLoginPage();
  }

  return response;
}

export const defaultFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const headers = {
    ...init?.headers
  }

  return await fetch(input, {
    ...init,
    headers
  });
}

export const logoutUser = () => {
  //document.cookie = "auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/login';
  Sentry.setUser(null)
}

export const loginRedirect = (router: NextRouter, redirectURL: string) => {
  if (redirectURL.includes("login")) {
    return;
  }

  if (redirectURL.includes("register") || redirectURL.includes("reset-password")) {
    router.push(`/login`)
  } else {
    router.push(`/login?redirectURL=${redirectURL}`)
  }
}

export const registerRedirect = (router: NextRouter) => {
  router.push(`/register`)
}
